import {
  ProductVariant,
  ProductVariantAttributeWithValues,
} from "../types/ProductVariant";

export interface VariantAttributeDisplay {
  name: string;
  value: string;
}

interface ProductVariantDetailLike {
  productoVariante: ProductVariant;
  productoVarianteAtributoValores?: {
    nombreAtributo?: string;
    nombreAtributoValor?: string;
    name?: string;
    value?: string;
  }[];
}

type ProductVariantWithAttributes = ProductVariant & {
  atributos?: {
    nombreAtributo?: string;
    nombreAtributoValor?: string;
    valor?: string;
  }[];
};

const normalizeToken = (value?: string | null) =>
  (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

export const buildVariantAttributeDisplay = (
  variantAttributes: ProductVariantAttributeWithValues[],
  selectedAttributes: Record<string, string>
): VariantAttributeDisplay[] =>
  Object.entries(selectedAttributes)
    .map(([attributeId, valueId]) => {
      const attribute = variantAttributes.find((item) => item.idProductoAtributo === attributeId);
      const value = attribute?.valores.find((item) => item.idProductoAtributoValor === valueId);
      return {
        name: attribute?.nombreAtributo ?? "",
        value: value?.nombreValor ?? "",
      };
    })
    .filter((item) => item.name && item.value);

const getDetailValuesByAttribute = (
  detail: ProductVariantDetailLike | null | undefined,
  attributeName: string
) =>
  (detail?.productoVarianteAtributoValores || []).filter(
    (item) => (item.nombreAtributo || item.name) === attributeName
  );

const inferValueFromVariantSku = (
  values: ProductVariantAttributeWithValues["valores"],
  variant: ProductVariant | null | undefined,
  allVariants: ProductVariant[]
) => {
  if (!variant) return undefined;

  const variantSku = normalizeToken(variant.SKU);
  const directMatches = values.filter((value) => variantSku.includes(normalizeToken(value.nombreValor)));
  if (directMatches.length === 1) return directMatches[0];

  const currentId = variant.id;
  const valuesUsedByOtherVariants = new Set<string>();
  allVariants
    .filter((item) => item.id !== currentId)
    .forEach((item) => {
      const sku = normalizeToken(item.SKU);
      values.forEach((value) => {
        if (sku.includes(normalizeToken(value.nombreValor))) {
          valuesUsedByOtherVariants.add(value.idProductoAtributoValor);
        }
      });
    });

  const valuesNotUsedByOtherVariants = values.filter(
    (value) => !valuesUsedByOtherVariants.has(value.idProductoAtributoValor)
  );

  return valuesNotUsedByOtherVariants.length === 1 ? valuesNotUsedByOtherVariants[0] : undefined;
};

export const resolveInitialVariantSelection = (
  variantAttributes: ProductVariantAttributeWithValues[],
  detail: ProductVariantDetailLike | null | undefined,
  allVariants: ProductVariant[]
) => {
  const selectedAttributes: Record<string, string> = {};

  variantAttributes.forEach((attribute) => {
    const detailValues = getDetailValuesByAttribute(detail, attribute.nombreAtributo);
    const uniqueDetailValues = Array.from(
      new Set(detailValues.map((item) => item.nombreAtributoValor || item.value).filter(Boolean))
    );

    let selectedValue = uniqueDetailValues.length === 1
      ? attribute.valores.find((item) => item.nombreValor === uniqueDetailValues[0])
      : undefined;

    if (!selectedValue) {
      selectedValue = inferValueFromVariantSku(attribute.valores, detail?.productoVariante, allVariants);
    }

    if (selectedValue) {
      selectedAttributes[attribute.idProductoAtributo] = selectedValue.idProductoAtributoValor;
    }
  });

  return {
    selectedAttributes,
    selectedVariantAttributes: buildVariantAttributeDisplay(variantAttributes, selectedAttributes),
  };
};

const getVariantAttributeValues = (variant: ProductVariantWithAttributes) =>
  (variant.atributos || []).map((attribute) => attribute.valor || attribute.nombreAtributoValor || "");

export const pickVariantForSelectedAttributes = (
  variants: ProductVariant[],
  variantAttributes: ProductVariantAttributeWithValues[],
  selectedAttributes: Record<string, string>,
  defaultVariantId?: string
) => {
  if (variants.length <= 1) return variants[0] ?? null;

  const selectedLabels = buildVariantAttributeDisplay(variantAttributes, selectedAttributes);
  const selectedTokens = selectedLabels.map((item) => normalizeToken(item.value)).filter(Boolean);

  const scoredVariants = variants.map((variant) => {
    const variantWithAttributes = variant as ProductVariantWithAttributes;
    const sku = normalizeToken(variant.SKU);
    const attributeValues = getVariantAttributeValues(variantWithAttributes).map(normalizeToken);

    const score = selectedTokens.reduce((total, token) => {
      if (sku.includes(token)) return total + 3;
      if (attributeValues.some((value) => value === token)) return total + 5;
      return total;
    }, 0);

    return { variant, score };
  });

  const bestMatch = scoredVariants
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)[0]?.variant;

  if (bestMatch) return bestMatch;

  return variants.find((variant) => variant.id === defaultVariantId) ?? variants[0] ?? null;
};
